import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useParams } from 'react-router';
import { addDays } from '../../lib/dates/iso.ts';
import { describeDays, formatDayYear, formatRange } from '../../lib/format/dates.ts';
import { formatDayNear } from '../../lib/format/near.ts';
import { answerState } from '../../lib/requests/answer.ts';
import { requestStanding, type RequestStanding } from '../../lib/requests/cells.ts';
import { REQUEST_LIMITS, checkRequests, hasSpecialWeeks, summarizeRequests, type RequestCheck, type RequestKind } from '../../lib/requests/limits.ts';
import type { Week } from '../../lib/weeks/weeks.ts';
import { ApiFailure, api, type AdminEntry, type AvailabilityKind, type PlainWeek, type RequestPeriod, type RequestView } from '../api.ts';
import { Notice } from '../components/Notice.tsx';
import { ScrollRegion } from '../components/ScrollRegion.tsx';
import { WeekBadges } from '../components/WeekTable.tsx';
import { copy } from '../copy/en-CA.ts';
import { useSession } from '../session.tsx';

const c = copy.requests.period;
const n = copy.requests.nothingNeeded;
const KINDS: RequestKind[] = ['vacation', 'preferred', 'avoid'];
const BAR_DETAILS_ID = 'request-bar-details';

function asWeek(w: PlainWeek): Week {
  return { ...w };
}

function setByAdminId(weekStart: string): string {
  return `set-by-admin-${weekStart}`;
}

/**
 * Asks before the person leaves with choices they have not submitted: by a
 * link or tab inside the app (the router's blocker), or by closing or
 * reloading the browser tab (the browser's own "leave site?" prompt).
 */
function useConfirmLeaving(unsaved: boolean): void {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => unsaved && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm(c.leaveUnsaved)) blocker.proceed();
    else blocker.reset();
  }, [blocker]);
  useEffect(() => {
    if (!unsaved) return;
    const warn = (e: BeforeUnloadEvent) => {
      // The browser shows its own wording; a page cannot choose it.
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [unsaved]);
}

/** A request's status, or "Replaced by an admin's entry" where an admin entered the same week: what counts is never contradicted. */
function StatusBadge({ status }: { status: RequestStanding }) {
  return <span className={`badge ${status === 'approved' ? 'ok' : status === 'declined' ? 'conf' : status === 'pending' ? 'pending' : ''}`}>{c.status[status]}</span>;
}

function WarningIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * A physician's requests for one period: one choice per week, the limits
 * checked as they go (with the same pure module the server runs), and the
 * counts and any problem kept in view at the bottom of the screen so they
 * are never scrolled out of sight. Weeks an admin already entered for them
 * (a leave, a vacation agreed in person) are shown and cannot be chosen.
 * Once the period is closed, or for an account with no physician, there is
 * nothing to choose: the page shows only what is on record.
 *
 * A physician who needs nothing this time says so ("Nothing needed this
 * time", an empty submission), so the admin knows they have answered
 * (owner, 24 October 2026). It is offered until they first answer.
 */
export function RequestPeriodPage() {
  const { id = '' } = useParams();
  const { user } = useSession();
  const [period, setPeriod] = useState<RequestPeriod | null>(null);
  const [weeks, setWeeks] = useState<PlainWeek[]>([]);
  const [saved, setSaved] = useState<RequestView[]>([]);
  const [entries, setEntries] = useState<AdminEntry[]>([]);
  const [choice, setChoice] = useState<Record<string, RequestKind>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // The day this physician last answered (Toronto's), and today, from the server.
  const [submittedOn, setSubmittedOn] = useState<string | null>(null);
  const [today, setToday] = useState('');
  // Set by pressing "Nothing needed this time", so the bar announces the answer and takes the focus.
  const [justAnswered, setJustAnswered] = useState(false);
  // The week whose last button just went away (a request cleared on a week an
  // admin entered): focus moves to the text that replaced it, not to the page.
  const [refocus, setRefocus] = useState<string | null>(null);

  const physicianId = user?.physicianId ?? null;
  useEffect(() => {
    api
      .requestPeriod(id)
      .then((r) => {
        // Only the signed-in physician's own requests are ever choices on this
        // screen. The server already sends no one else's; this keeps it so if
        // that ever changes, since saving here replaces all of this
        // physician's requests with what the screen holds.
        const own = r.requests.filter((q) => q.physicianId === physicianId);
        setPeriod(r.period);
        setWeeks(r.weeks);
        setSaved(own);
        setEntries(r.entries);
        setChoice(Object.fromEntries(own.map((q) => [q.weekStart, q.kind])));
        setSubmittedOn(r.submittedOn);
        setToday(r.today);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
      });
  }, [id, physicianId]);

  useEffect(() => {
    if (refocus === null) return;
    document.getElementById(setByAdminId(refocus))?.focus();
    setRefocus(null);
  }, [refocus]);

  const requests = useMemo(() => Object.entries(choice).map(([weekStart, kind]) => ({ weekStart, kind })), [choice]);
  const check = useMemo(() => checkRequests(weeks.map(asWeek), requests), [weeks, requests]);
  // Christmas/New Year rules are explained only where the period has such a week.
  const special = useMemo(() => hasSpecialWeeks(weeks), [weeks]);
  const savedByWeek = useMemo(() => new Map(saved.map((q) => [q.weekStart, q])), [saved]);
  const entryByWeek = useMemo(() => new Map<string, AvailabilityKind>(entries.map((e) => [e.weekStart, e.kind])), [entries]);
  const dirty = useMemo(() => {
    const before = [...saved].map((q) => `${q.weekStart}:${q.kind}`).sort().join('|');
    const after = requests.map((q) => `${q.weekStart}:${q.kind}`).sort().join('|');
    return before !== after;
  }, [saved, requests]);
  const isPhysician = physicianId !== null;
  const canEdit = isPhysician && period?.open === true;
  useConfirmLeaving(canEdit && dirty);
  const problemWeeks = new Set(check.problems.flatMap((p) => p.weekStarts));
  const warningWeeks = new Set(check.warnings.flatMap((w) => w.weekStarts));
  // A declined request on a week an admin entered shows as replaced, not declined.
  const anyDeclined = saved.some((q) => requestStanding(q.status, entryByWeek.has(q.weekStart)) === 'declined');
  // Owner, 24 October 2026: a physician who is also an admin approves their own requests, like anyone's.
  const adminToo = isPhysician && user?.isAdmin === true;
  const problemText = (kind: string): string => (special ? undefined : c.problemsNoSpecialWeeks[kind]) ?? c.problems[kind] ?? kind;
  // An empty answer stays an answer while the person goes on to choose weeks, until they submit them.
  const answer = answerState(saved.length, submittedOn);
  const nothingSentence = answer === 'nothing-needed' && submittedOn !== null && period !== null ? n.answered(formatDayNear(submittedOn, today), formatDayNear(period.dueDate, today)) : null;

  /** Submits the choices, or none at all for "Nothing needed this time". */
  const submit = async (list: { weekStart: string; kind: RequestKind }[]) => {
    setError(null);
    setBusy(true);
    setDone(false);
    setJustAnswered(false);
    try {
      const r = await api.submitRequests(id, list);
      setSaved(r.requests);
      setSubmittedOn(r.submittedOn);
      setDone(true);
      setJustAnswered(list.length === 0);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    } finally {
      setBusy(false);
    }
  };

  if (error && !period) return <Notice kind="error">{error}</Notice>;
  if (!period) return <p className="muted">{c.loading}</p>;

  const title = (
    <div className="page-title">
      <p className="muted small">
        <Link to="/requests">{copy.requests.title}</Link>
      </p>
      <h1>{period.name}</h1>
      <p>{canEdit ? c.intro(formatDayYear(period.dueDate)) : isPhysician ? c.closedIntro : c.notPhysician}</p>
      {adminToo ? <p>{c.adminNote}</p> : null}
    </div>
  );

  // Nothing to choose: the guide and the week-by-week buttons would only look
  // clickable. Show what is on record instead.
  if (!canEdit) {
    return (
      <>
        {title}
        {isPhysician ? (
          <>
            {anyDeclined ? <Notice kind="info">{c.declinedNote}</Notice> : null}
            <RecordedRequests
              periodName={period.name}
              weeks={weeks}
              requests={saved}
              entries={entries}
              nothingNeededOn={answer === 'nothing-needed' && submittedOn !== null ? formatDayNear(submittedOn, today) : null}
            />
          </>
        ) : null}
      </>
    );
  }

  const limitLines = [
    c.limits.vacation,
    ...(special ? [c.limits.special] : []),
    c.limits.preferAvoid,
    c.limits.together,
    c.limits.consecutive,
    // The server checks only what the physician submits (checkRequests on the
    // submitted requests), so an admin's entries never use up the allowance.
    ...(entries.length > 0 ? [c.limits.adminEntries] : []),
  ];

  return (
    <>
      {title}
      {nothingSentence ? <Notice kind="success">{nothingSentence}</Notice> : null}

      <section className="card request-guide" aria-label={c.meaningsTitle}>
        <div className="stack">
          <h2>{c.meaningsTitle}</h2>
          <ul className="plain meanings">
            {c.meanings.map((m) => (
              <li key={m.kind}>
                <span className={`badge req-${m.kind}`}>{c.kinds[m.kind]}</span> {m.text}
              </li>
            ))}
          </ul>
          {entries.length > 0 ? <p className="muted small">{c.adminEntries}</p> : null}
        </div>
        <div className="stack">
          <h2>{c.limitsTitle}</h2>
          <ul className="plain limits">
            {limitLines.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      </section>
      {anyDeclined ? <Notice kind="info">{c.declinedNote}</Notice> : null}

      <div className="card table-card">
        <div className="table-region request-table" role="region" aria-label={period.name} tabIndex={0} style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>{c.weekColumn}</th>
                <th>{copy.schedule.columns.notes}</th>
                <th>{copy.requests.title}</th>
                <th>{copy.requests.columns.status}</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => {
                const current = choice[w.weekStart] ?? 'none';
                const existing = savedByWeek.get(w.weekStart);
                const entry = entryByWeek.get(w.weekStart);
                // Where an admin entered the week, their entry counts instead of the request: say so, not "Approved".
                const status = existing?.kind === current ? requestStanding(existing.status, entry !== undefined) : null;
                const cls = problemWeeks.has(w.weekStart)
                  ? 'conf'
                  : warningWeeks.has(w.weekStart)
                    ? 'warn'
                    : entry
                      ? 'entry'
                      : current === 'none'
                        ? ''
                        : `req-${current}`;
                // Empty cells render nothing at all, so a phone's card has no blank gap.
                const hasNotes = w.kind !== 'regular' || entry !== undefined;
                // A week an admin entered cannot be chosen, so it shows no buttons that
                // would only look clickable; a request already on it keeps its one
                // button, so it can still be cleared.
                const kinds = entry === undefined ? KINDS : KINDS.filter((k) => k === current);
                const range = formatRange(w.onCallStart, w.onCallEnd);
                return (
                  <tr key={w.weekStart} className={cls}>
                    <td>
                      <div className="range">{range}</div>
                      <div className="muted small">{describeDays(w)}</div>
                    </td>
                    <td>
                      {hasNotes ? (
                        <span className="badges">
                          <WeekBadges week={{ ...w, specialOverride: null, physicianId: null, conflicts: [], manual: false }} />
                          {entry ? <span className={`badge entry-${entry}`}>{c.entry[entry]}</span> : null}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {kinds.length === 0 ? (
                        <span id={setByAdminId(w.weekStart)} className="muted small set-by-admin" tabIndex={-1}>
                          {c.setByAdmin}
                        </span>
                      ) : (
                        <div className="seg-group" role="group" aria-label={c.weekLabel(range)}>
                          {kinds.map((k) => (
                            <button
                              key={k}
                              type="button"
                              aria-pressed={current === k}
                              className={`seg small ${current === k ? `on ${k}` : ''}`}
                              onClick={() => {
                                // One choice per week: pressing the active one clears it.
                                setDone(false);
                                setJustAnswered(false);
                                if (entry !== undefined && current === k) setRefocus(w.weekStart);
                                setChoice((prev) => {
                                  const rest = Object.fromEntries(Object.entries(prev).filter(([ws]) => ws !== w.weekStart));
                                  return current === k ? rest : { ...rest, [w.weekStart]: k };
                                });
                              }}
                            >
                              {c.kinds[k]}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>{status ? <StatusBadge status={status} /> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <RequestBar
        check={check}
        special={special}
        problemText={problemText}
        error={error}
        submitted={done && !dirty}
        dirty={dirty}
        busy={busy}
        onSubmit={() => void submit(requests)}
        // Before any answer, with nothing chosen, Submit has nothing to send: the bar offers this instead.
        offerNothing={answer === 'not-answered' && !dirty}
        answeredNothing={answer === 'nothing-needed' && !dirty}
        announceNothing={justAnswered && !dirty ? nothingSentence : null}
        onNothing={() => void submit([])}
      />
    </>
  );
}

/**
 * The counts, notices and Submit button, kept in view at the bottom of the
 * screen. From 720 px wide everything shows. On a phone it would cover
 * almost half the screen, so it folds to one line: a button that sums up
 * the state in words (and opens the full counts and notices), and Submit.
 * Being over a limit is said in words, with the conflict colour and an
 * icon, never by colour alone. While it is folded, a hidden copy of the
 * problems and the submit result sits in the bar's live region, so a
 * screen reader still hears them.
 *
 * Before the physician has answered, with nothing chosen, Submit would have
 * nothing to send, so its place holds "Nothing needed this time" (an empty
 * submission). Once that is the answer, the bar says so; choosing a week
 * brings Submit back.
 */
function RequestBar({
  check,
  special,
  problemText,
  error,
  submitted,
  dirty,
  busy,
  onSubmit,
  offerNothing,
  answeredNothing,
  announceNothing,
  onNothing,
}: {
  check: RequestCheck;
  special: boolean;
  problemText: (kind: string) => string;
  error: string | null;
  submitted: boolean;
  dirty: boolean;
  busy: boolean;
  onSubmit: () => void;
  /** Offer "Nothing needed this time" in Submit's place. */
  offerNothing: boolean;
  /** The answer on record is "nothing needed", and nothing is chosen since. */
  answeredNothing: boolean;
  /** The full sentence to announce, just after the person answered "Nothing needed this time"; else null. */
  announceNothing: string | null;
  onNothing: () => void;
}) {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const answeredRef = useRef<HTMLDivElement>(null);
  const s = summarizeRequests(check);
  const counts = c.bar.summary({ ...s, unsaved: dirty });
  const tone = s.overLimit || error !== null ? 'conf' : submitted || answeredNothing ? 'ok' : '';
  const line = s.overLimit
    ? open
      ? c.bar.overLimitOpen
      : c.bar.overLimit
    : error !== null
      ? open
        ? c.bar.failedOpen
        : c.bar.failed
      : answeredNothing
        ? n.line
        : submitted
          ? c.bar.submitted(counts)
          : counts;
  const announce = [
    ...check.problems.map((p) => problemText(p.kind)),
    ...(error !== null ? [error] : []),
    ...(announceNothing !== null ? [announceNothing] : submitted && !answeredNothing ? [c.saved] : []),
  ];

  // The button just pressed is gone (Submit is back in its place, with nothing
  // to send), so the focus goes to what says the answer was taken: the folded
  // line on a phone, the notice on a wide window, where that line is hidden.
  useEffect(() => {
    if (announceNothing === null) return;
    const toggle = toggleRef.current;
    if (toggle && toggle.offsetParent !== null) toggle.focus();
    else answeredRef.current?.focus();
  }, [announceNothing]);

  return (
    <div className={`sticky-bar${open ? ' open' : ''}${offerNothing ? ' offer-nothing' : ''}`} role="region" aria-label={copy.requests.title} aria-live="polite">
      {/* Scrolls inside itself on a phone if it is taller than half the screen, so it can be reached by keyboard then. */}
      <div className="bar-details" id={BAR_DETAILS_ID} tabIndex={open ? 0 : undefined}>
        <div className="counts">
          <span className={check.counts.vacation > REQUEST_LIMITS.vacation ? 'over' : ''}>{c.counts.vacation(check.counts.vacation, REQUEST_LIMITS.vacation)}</span>
          <span className={check.counts.vacationLongWeekends > REQUEST_LIMITS.vacationLongWeekends ? 'over' : ''}>
            {c.counts.vacationLongWeekends(check.counts.vacationLongWeekends, REQUEST_LIMITS.vacationLongWeekends)}
          </span>
          {special ? (
            <span className={check.counts.vacationSpecial > REQUEST_LIMITS.vacationSpecial ? 'over' : ''}>{c.counts.vacationSpecial(check.counts.vacationSpecial, REQUEST_LIMITS.vacationSpecial)}</span>
          ) : null}
          <span className={check.counts.preferred > REQUEST_LIMITS.preferred ? 'over' : ''}>{c.counts.preferred(check.counts.preferred, REQUEST_LIMITS.preferred)}</span>
          <span className={check.counts.avoid > REQUEST_LIMITS.avoid ? 'over' : ''}>{c.counts.avoid(check.counts.avoid, REQUEST_LIMITS.avoid)}</span>
          <span className={check.counts.vacationPlusAvoid > REQUEST_LIMITS.vacationPlusAvoid ? 'over' : ''}>
            {c.counts.vacationPlusAvoid(check.counts.vacationPlusAvoid, REQUEST_LIMITS.vacationPlusAvoid)}
          </span>
        </div>
        {check.problems.length > 0 ? (
          <Notice kind="error">
            <ul className="plain">
              {check.problems.map((p) => (
                <li key={p.kind}>{problemText(p.kind)}</li>
              ))}
            </ul>
          </Notice>
        ) : null}
        {check.warnings.length > 0 ? (
          <Notice kind="info">
            <ul className="plain">
              {check.warnings.map((w) => (
                <li key={w.kind}>{c.warnings[w.kind] ?? w.kind}</li>
              ))}
            </ul>
          </Notice>
        ) : null}
        {error ? <Notice kind="error">{error}</Notice> : null}
        {submitted && !answeredNothing ? <Notice kind="success">{c.saved}</Notice> : null}
        {answeredNothing ? (
          <div ref={answeredRef} tabIndex={-1} className="bar-answered">
            <Notice kind="success">{n.line}</Notice>
          </div>
        ) : null}
        {offerNothing ? <p className="muted small">{n.hint}</p> : null}
      </div>
      <p className="bar-announce visually-hidden">{announce.join(' ')}</p>
      <div className="bar-line">
        <button
          ref={toggleRef}
          type="button"
          className={`bar-toggle ${tone}`}
          aria-expanded={open}
          aria-controls={BAR_DETAILS_ID}
          onClick={() => {
            setOpen((o) => !o);
          }}
        >
          {tone === 'conf' ? <WarningIcon /> : tone === 'ok' ? <CheckIcon /> : null}
          <span className="bar-toggle-text">{line}</span>
          <svg className="chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 15 6-6 6 6" />
          </svg>
        </button>
        {offerNothing ? (
          <button type="button" className="btn nothing-btn" disabled={busy} aria-label={busy ? undefined : n.button} onClick={onNothing}>
            {busy ? (
              n.sending
            ) : (
              <>
                <span className="label-wide">{n.button}</span>
                <span className="label-short">{n.buttonShort}</span>
              </>
            )}
          </button>
        ) : (
          <button type="button" className="btn primary" disabled={busy || !check.ok || !dirty} aria-label={busy ? undefined : c.submit} onClick={onSubmit}>
            {busy ? (
              c.submitting
            ) : (
              <>
                <span className="label-wide">{c.submit}</span>
                <span className="label-short">{c.submitShort}</span>
              </>
            )}
          </button>
        )}
        {dirty ? <span className="muted small unsaved">{c.unsaved}</span> : null}
      </div>
    </div>
  );
}

interface RecordedRow {
  key: string;
  weekStart: string;
  /** The badge's colour class: the same as the choice, or the admin's entry, on the open screen. */
  badge: string;
  label: string;
  /** Null for an admin's entry, which never waited for approval; "replaced" for a request on a week an admin entered. */
  status: RequestStanding | null;
}

/**
 * A closed period, read only: this physician's requests and what an admin
 * entered for them, by week. Admin entries have no status: they were never
 * waiting for approval. `nothingNeededOn`: the day they answered "Nothing
 * needed this time", said in place of "You made no requests".
 */
function RecordedRequests({
  periodName,
  weeks,
  requests,
  entries,
  nothingNeededOn,
}: {
  periodName: string;
  weeks: PlainWeek[];
  requests: RequestView[];
  entries: AdminEntry[];
  nothingNeededOn: string | null;
}) {
  const none = nothingNeededOn !== null ? n.closed(nothingNeededOn) : c.readOnly.none;
  const weekByStart = new Map(weeks.map((w) => [w.weekStart, w]));
  const entryWeeks = new Set(entries.map((e) => e.weekStart));
  const rows: RecordedRow[] = [
    ...requests.map((q) => ({ key: `request-${q.id}`, weekStart: q.weekStart, badge: `req-${q.kind}`, label: c.kinds[q.kind], status: requestStanding(q.status, entryWeeks.has(q.weekStart)) })),
    ...entries.map((e) => ({ key: `entry-${e.weekStart}`, weekStart: e.weekStart, badge: `entry-${e.kind}`, label: c.entry[e.kind], status: null })),
  ];
  // ISO dates sort as text. Sorting is stable, so on a week with both, the request comes first.
  rows.sort((a, b) => (a.weekStart < b.weekStart ? -1 : a.weekStart > b.weekStart ? 1 : 0));

  if (rows.length === 0) {
    return (
      <section className="card">
        <p className="empty">{none}</p>
      </section>
    );
  }
  return (
    <>
      {requests.length === 0 ? <p>{none}</p> : null}
      <div className="card table-card">
        <ScrollRegion label={c.readOnly.label(periodName)} className="request-table request-record" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>{c.readOnly.columns.week}</th>
                <th>{c.readOnly.columns.request}</th>
                <th>{c.readOnly.columns.status}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const w = weekByStart.get(r.weekStart);
                return (
                  <tr key={r.key}>
                    <td>
                      <div className="range">{w ? formatRange(w.onCallStart, w.onCallEnd) : formatRange(r.weekStart, addDays(r.weekStart, 6))}</div>
                      {w ? <div className="muted small">{describeDays(w)}</div> : null}
                    </td>
                    <td>
                      <span className={`badge ${r.badge}`}>{r.label}</span>
                    </td>
                    <td>{r.status ? <StatusBadge status={r.status} /> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollRegion>
      </div>
    </>
  );
}
